import React, {useState, useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {useDropzone} from 'react-dropzone';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import Instructions from './Instructions';
import SnackbarSuccess from './SnackbarSuccess';
import AutoCompleteTags from './AutoCompleteTags';
import {BluePillButton} from '../../common/components/BluePillButton';
import {uploadFileToS3} from '../../services/s3Service';
import {UPLOAD_TYPE, MIME_TYPE, MAX_UPLOAD_FILE_SIZE, APP_READING, FEATURES} from '../../constants';
import IconFileUpload from '../../components/icons/FileUpload';
import LinearProgressWithLabel from '../../components/LinearProgressWithLabel';
import {createExercise, updateExercise} from '../../redux/slices/builder';
import {selectUserGroup} from '../../redux/slices/user';

import styles from '../css/CreateEditReadingExercise.module.css';
import baseStyles from '../../css/Base.module.css';

const EIL_OPTIONS = Array.from(Array(28).keys()).map((index) => (
  <MenuItem key={index + 1} value={index + 1}>
    {index + 1}
  </MenuItem>
));
const FP_OPTIONS = Array(26)
  .fill()
  .map((_, i) => String.fromCharCode('A'.charCodeAt(0) + i))
  .map((letter) => (
    <MenuItem key={letter} value={letter}>
      {letter}
    </MenuItem>
  ));
const LEXILE_OPTIONS = [
  <MenuItem key="BR" value="BR">
    BR (Beginning Reader)
  </MenuItem>,
  <MenuItem key="50L" value="50L">
    50L
  </MenuItem>,
];
for (let i = 100; i <= 1300; i += 10) {
  LEXILE_OPTIONS.push(<MenuItem key={`${i}L`} value={`${i}L`}>{`${i}L`}</MenuItem>);
}

export default function CreateEditReadingExercise({exercise}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [mode, setMode] = useState(null); // CREATE, EDIT
  const [isSaving, toggleIsSaving] = useState(false);
  const [name, setName] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [wordCount, setWordCount] = useState(null);
  const [genre, setGenre] = useState(null);
  const [language, setLanguage] = useState(null);
  const [description, setDescription] = useState(null);
  const [levelEIL, setLevelEIL] = useState(null);
  const [levelFP, setLevelFP] = useState(null);
  const [levelLexile, setLevelLexile] = useState(null);
  const [visibility, setVisibility] = useState('');
  const [tags, setTags] = useState([]);
  const [instructionsHTML, setInstructionsHTML] = useState(null);
  const [instructionsPlainText, setInstructionsPlainText] = useState(null);
  const [isSnackbarOpen, toggleSnackbar] = useState(false);

  const userGroup = useSelector(selectUserGroup);
  const {
    tier: {features},
  } = userGroup;
  const visibilityFeatures = features.filter((feature) => feature.startsWith('VISIBILITY_'));

  useEffect(() => {
    if (!exercise) {
      setMode('CREATE');
      setVisibility(getDefaultVisibility());
    } else {
      setMode('EDIT');
      setExistingExerciseValues();
    }
  }, [exercise?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  function getDefaultVisibility() {
    // Auto set visibility when only one option is available
    if (visibilityFeatures.length === 1) {
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_PRIVATE) return 'PRIVATE';
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_USER_GROUP) return 'USER_GROUP';
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_PUBLIC) return 'PUBLIC';
    } else if (visibilityFeatures.includes(FEATURES.VISIBILITY_PRIVATE)) return 'PRIVATE';
    return '';
  }

  function setExistingExerciseValues() {
    const {config} = exercise;
    setName(exercise.name);
    setDescription(exercise.description);
    setVisibility(exercise.visibility);
    setTags(exercise.tags);
    setGenre(config.genre);
    setLanguage(config.language);
    setLevelEIL(config.levelEIL);
    setLevelFP(config.levelFP);
    setLevelLexile(config.levelLexile);
    setWordCount(config.wordCount);
    setInstructionsHTML(exercise?.instructions?.html);
    setInstructionsPlainText(exercise?.instructions?.plainText);
  }

  function handleDeleteSelectedFile() {
    setFile(null);
  }

  function handleNameChange(event) {
    setName(event.target.value);
  }

  function handleVisibilityChanged(event) {
    setVisibility(event.target.value);
  }

  function isSaveButtonEnabled() {
    if (mode === 'EDIT') return !!name && !!genre && !!language && !!visibility && !isSaving;
    return !!name && !!file && !!genre && !!language && !!visibility && !isSaving;
  }

  async function handleSave(exercisePayload) {
    if (mode === 'CREATE') {
      const newExercise = await dispatch(
        createExercise(
          exercisePayload.name,
          exercisePayload.type,
          exercisePayload.description,
          exercisePayload.visibility,
          exercisePayload.config,
          exercisePayload.tags,
        ),
      );
      history.push(`/builder/exercise/${newExercise._id}`); // Go into "edit" mode
    } else if (mode === 'EDIT') {
      await dispatch(updateExercise(exercise._id, exercisePayload));
    }

    toggleSnackbar(true);
    toggleIsSaving(false);
  }

  function handleClickCancel() {
    history.push(`/builder/exercises?type=${APP_READING}`);
  }

  async function handleClickSave() {
    toggleIsSaving(true);

    let url = exercise?.config?.url;
    if (file) {
      url = await uploadFileToS3(UPLOAD_TYPE.EXERCISE_READING, file, MIME_TYPE.PDF, (percentComplete) =>
        setUploadPercent(percentComplete),
      );
    }

    const tagIDs = tags.map((tag) => tag._id);
    const exercisePayload = {
      type: APP_READING,
      name,
      description,
      visibility,
      tags: tagIDs,
      config: {
        url,
        genre,
        language,
        levelEIL,
        levelFP,
        levelLexile,
        wordCount,
      },
    };
    handleSave(exercisePayload);
  }

  const onDrop = useCallback((acceptedFiles) => {
    setFile(acceptedFiles[0]);
  }, []);

  // https://react-dropzone.js.org
  // https://upmostly.com/tutorials/react-dropzone-file-uploads-react
  const {getRootProps, getInputProps, fileRejections} = useDropzone({
    onDrop,
    accept: {[MIME_TYPE.PDF]: ['.pdf']},
    minSize: 0,
    maxSize: MAX_UPLOAD_FILE_SIZE,
  });

  function renderTabDetails() {
    const isFileTooLarge = fileRejections[0]?.file.size > MAX_UPLOAD_FILE_SIZE;
    const isWrongFileType = fileRejections[0]?.errors[0]?.code === 'file-invalid-type';

    return (
      <>
        <div className="p-15">
          <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
            Basic Info
          </Typography>

          <Grid container>
            <Grid item xs={12} md={6} xl={4}>
              <TextField
                value={name ?? ''}
                onChange={handleNameChange}
                label="Title"
                variant="standard"
                autoFocus
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Grid container className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                File Upload
              </Typography>
              <div className={styles['file-upload-dropzone']}>
                <div {...getRootProps()}>
                  <input type="file" {...getInputProps()} />
                  <Typography>Drag &amp; drop PDF here...</Typography>
                  <Typography className="m-b-5">or just click</Typography>
                  <IconFileUpload />
                </div>
              </div>
              {isFileTooLarge && (
                <div className="clr-danger-300">
                  ! File is too large. File size is limited to {MAX_UPLOAD_FILE_SIZE / 1000000}MB.
                </div>
              )}
              {isWrongFileType && <div className="clr-danger-300">! Incorrect file type. File must be a PDF.</div>}
              {file && <Chip label={file.name} onDelete={handleDeleteSelectedFile} />}
              {uploadPercent > 0 && <LinearProgressWithLabel percent={uploadPercent} />}
            </Grid>
          </Grid>

          <div className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <TextField
                      variant="standard"
                      type="number"
                      value={wordCount ?? ''}
                      onChange={(event) => setWordCount(event.target.value)}
                      label="Word count"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="genre-label" variant="standard" required>
                      Genre
                    </InputLabel>
                    <Select
                      labelId="genre-label"
                      variant="standard"
                      value={genre ?? ''}
                      onChange={(event) => setGenre(event.target.value)}
                    >
                      <MenuItem value="Fiction">Fiction</MenuItem>
                      <MenuItem value="Nonfiction">Nonfiction</MenuItem>
                      <MenuItem value="Decodable">Decodable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="language-label" variant="standard" required>
                      Language
                    </InputLabel>
                    <Select
                      labelId="language-label"
                      variant="standard"
                      value={language ?? ''}
                      onChange={(event) => setLanguage(event.target.value)}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Spanish">Spanish</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </div>

          <Grid item xs={12} md={6} xl={4}>
            <TextField
              variant="standard"
              className="m-t-10"
              value={description ?? ''}
              onChange={(event) => setDescription(event.target.value)}
              label="Description"
              multiline
              fullWidth
            />
          </Grid>

          <div className="m-t-30">
            <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
              Level
            </Typography>
            <Typography variant="subtitle2" className="fnt-bold">
              Select all that apply
            </Typography>
            <Grid item xs={12} md={6} xl={4}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="level-eil-label" variant="standard">
                      EIL
                    </InputLabel>
                    <Select
                      labelId="level-eil-label"
                      variant="standard"
                      value={levelEIL ?? ''}
                      onChange={(event) => setLevelEIL(event.target.value)}
                    >
                      {EIL_OPTIONS}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="level-fp-label" variant="standard">
                      F&amp;P
                    </InputLabel>
                    <Select
                      labelId="level-fp-label"
                      variant="standard"
                      value={levelFP ?? ''}
                      onChange={(event) => setLevelFP(event.target.value)}
                    >
                      {FP_OPTIONS}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="level-lexile-label" variant="standard">
                      Lexile
                    </InputLabel>
                    <Select
                      labelId="level-lexile-label"
                      variant="standard"
                      value={levelLexile ?? ''}
                      onChange={(event) => setLevelLexile(event.target.value)}
                    >
                      {LEXILE_OPTIONS}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </div>

          {visibilityFeatures.length > 1 && (
            <div className="m-t-30">
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Visibility
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                You may keep this lesson for private use, or share for others to use.
              </Typography>
              <FormControl>
                <RadioGroup value={visibility} onChange={handleVisibilityChanged}>
                  {features.includes(FEATURES.VISIBILITY_PRIVATE) && (
                    <FormControlLabel value="PRIVATE" control={<Radio />} label="Private" />
                  )}
                  {features.includes(FEATURES.VISIBILITY_USER_GROUP) && (
                    <FormControlLabel value="USER_GROUP" control={<Radio />} label="Everyone" />
                  )}
                  {features.includes(FEATURES.VISIBILITY_PUBLIC) && (
                    <FormControlLabel value="PUBLIC" control={<Radio />} label="Public" />
                  )}
                </RadioGroup>
              </FormControl>
            </div>
          )}

          <Grid container className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Tags
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                Create a tag system to organize and find files and other information faster.
              </Typography>
              <AutoCompleteTags tags={tags} setTags={setTags} />
            </Grid>
          </Grid>
        </div>
        <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
          <Toolbar>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Button className={baseStyles['btn-pill']} onClick={handleClickCancel}>
                Cancel
              </Button>
              <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleClickSave}>
                {isSaving ? <CircularProgress size={25} /> : 'Save'}
              </BluePillButton>
            </Grid>
          </Toolbar>
        </AppBar>
        <Toolbar /> {/* Makes AppBar at the bottom of the page */}
      </>
    );
  }

  function handleChangeTab(event, newTabIndex) {
    setActiveTabIndex(newTabIndex);
  }

  async function handleSaveInstructions() {
    await dispatch(
      updateExercise(exercise._id, {instructions: {html: instructionsHTML, plainText: instructionsPlainText}}),
    );
    toggleSnackbar(true);
  }

  function renderTabInstructions() {
    return (
      <Instructions
        instructionsHTML={instructionsHTML}
        setInstructionsHTML={setInstructionsHTML}
        setInstructionsPlainText={setInstructionsPlainText}
        handleClickCancel={handleClickCancel}
        handleSaveInstructions={handleSaveInstructions}
      />
    );
  }

  return (
    <>
      <SnackbarSuccess text="Exercise Saved." isSnackbarOpen={isSnackbarOpen} toggleSnackbar={toggleSnackbar} />

      <Tabs
        className="m-b-30"
        value={activeTabIndex}
        onChange={handleChangeTab}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="DETAILS" />
        <Tab disabled={mode === 'CREATE'} label="INSTRUCTIONS" />
      </Tabs>

      {activeTabIndex === 0 && renderTabDetails()}
      {activeTabIndex === 1 && renderTabInstructions()}
    </>
  );
}
